import { useEffect, useRef } from 'react';
import { useIntl } from 'react-intl-next';

import type {
	ExtensionConfiguration,
	ToolbarExtensionConfiguration,
} from '@atlaskit/editor-plugin-selection-extension';
import AddIcon from '@atlaskit/icon/core/add';
import NoteIcon from '@atlaskit/icon/core/note';

import { sharedI18n } from '../sharedI18n.ts';

import { useContextualNoteCreation } from './useContextualNoteCreation.ts';
import { useContextualNotesList } from './useContextualNotesList.ts';

export const useNoteSelectionExtension = () => {
	const { formatMessage } = useIntl();
	const { handleCreateNoteClick, handleAddToNoteClick } = useContextualNoteCreation({
		source: 'useNoteSelectionExtension',
	});
	const { notesList } = useContextualNotesList();
	const notesListRef = useRef<
		{
			ari: string;
			label: string;
		}[]
	>([]);

	useEffect(() => {
		notesListRef.current = notesList;
	}, [notesList]);

	const toolbarConfiguration: ToolbarExtensionConfiguration = {
		getToolbarItem: () => ({
			icon: NoteIcon,
			tooltip: formatMessage(sharedI18n.addToNote),
		}),
		getMenuItems: () => {
			return [
				...notesListRef.current.map((note) => ({
					...note,
					icon: NoteIcon,
					onClick: () => handleAddToNoteClick(note.ari),
				})),
				{
					label: formatMessage(sharedI18n.createNote),
					icon: AddIcon,
					onClick: handleCreateNoteClick,
				},
				// MODES-7119 Add view all notes button
			];
		},
	};

	const noteSelectionExtension: ExtensionConfiguration = {
		key: 'note-contextual-create',
		source: 'first-party',
		inlineToolbar: toolbarConfiguration,
	};

	return {
		extensionList: [noteSelectionExtension],
	};
};
